import type {
  ConferenceAiForGoodListResp,
  ConferenceBrandItem,
  ConferenceAgendaDetailResp,
  ConferenceDynamicData,
  ConferenceExhibitionListResp,
  ConferenceGuestGroupListResp,
  ConferenceHighlightItem,
  ConferenceMomentItem,
  ConferenceMomentListResp,
  ConferenceNewsListResp,
  ConferencePartnerListResp,
  ConferenceSite,
  ConferenceThemeItem,
  ConferenceVideoListResp,
  ConferenceYearListResp,
  IndustryReportListResp,
} from './types';

const DOMESTIC_API_PREFIX = '/portalWebAPI/conference';
const OVERSEA_API_PREFIX = '/portalWebAPI/oversea/conference';

const CONFERENCE_REPORT_LIST_PATH = '/report/list';
const CONFERENCE_VIDEO_LIST_PATH = '/video/list';
const CONFERENCE_GUEST_GROUP_LIST_PATH = '/guest/group/list';
const CONFERENCE_YEAR_LIST_PATH = '/year/list';
const CONFERENCE_AGENDA_DETAIL_PATH = '/agenda/detail';
const CONFERENCE_THEME_PATH = '/theme';
const CONFERENCE_HIGHLIGHTS_PATH = '/highlights';
const CONFERENCE_BRANDS_PATH = '/brands';
const CONFERENCE_PARTNER_LIST_PATH = '/partner/list';
const CONFERENCE_AI_FOR_GOOD_LIST_PATH = '/ai-for-good/list';
const CONFERENCE_EXHIBITION_LIST_PATH = '/exhibition/list';
const CONFERENCE_NEWS_LIST_PATH = '/news/list';
const CONFERENCE_SIGNUP_REDIRECT_PATH =
  '/openTechPortalWebAPI/techforgood/conference/signup/redirect';
const DOMESTIC_MOMENT_DISPLAY_PATH =
  '/openTechPortalWebAPI/techforgood/conferenceMoment/v1/display';
const OVERSEA_MOMENT_DISPLAY_PATH =
  '/portalWebAPI/oversea/conferenceMoment/v1/display';
export const CONFERENCE_SIGNUP_PENDING_KEY = 'conference_signup_pending';

interface PortalBaseResp<T> {
  code?: number;
  msg?: string;
  data?: T;
}

const CONFERENCE_SIGNUP_LOGIN_CODES = new Set([4000, 4001, 4002, 403]);
const CONFERENCE_SIGNUP_FALLBACK_ERROR_MESSAGE = '报名跳转失败，请稍后重试';
const CONFERENCE_REQUEST_SUCCESS = Symbol('conferenceRequestSuccess');

const markConferenceRequestSuccess = <T,>(data: T): T => {
  if (data && typeof data === 'object') {
    Object.defineProperty(data, CONFERENCE_REQUEST_SUCCESS, {
      value: true,
    });
  }
  return data;
};

export const isConferenceRequestSuccessful = (data: unknown) =>
  Boolean(
    data &&
      typeof data === 'object' &&
      (data as Record<PropertyKey, unknown>)[CONFERENCE_REQUEST_SUCCESS]
  );

interface FetchConferenceDataParams {
  conferenceYear: number;
  site?: ConferenceSite;
  reportsData?: IndustryReportListResp;
  partnersData?: ConferencePartnerListResp;
}

export type ConferenceSignupButtonType = 'primary' | 'secondary';

export interface ConferenceSignupPendingAction {
  surveyUrl: string;
  button: ConferenceSignupButtonType;
  sourcePath: string;
  createdAt: number;
}

type ConferenceSignupRedirectResult =
  | {
      kind: 'ok';
      url: string;
    }
  | {
      kind: 'login';
    }
  | {
      kind: 'error';
      message: string;
      code?: number;
    };

const parseConferenceSignupRedirectPayload = (
  payload?: PortalBaseResp<{ url?: string }>
): ConferenceSignupRedirectResult => {
  if (payload?.code === 0 && payload?.data?.url) {
    return {
      kind: 'ok',
      url: payload.data.url,
    };
  }

  if (payload?.code && CONFERENCE_SIGNUP_LOGIN_CODES.has(payload.code)) {
    return { kind: 'login' };
  }

  return {
    kind: 'error',
    message: payload?.msg || CONFERENCE_SIGNUP_FALLBACK_ERROR_MESSAGE,
    code: payload?.code,
  };
};

const getApiPrefix = (site: ConferenceSite = 'zh') =>
  site === 'en' ? OVERSEA_API_PREFIX : DOMESTIC_API_PREFIX;

const getMomentDisplayPath = (site: ConferenceSite = 'zh') =>
  site === 'en' ? OVERSEA_MOMENT_DISPLAY_PATH : DOMESTIC_MOMENT_DISPLAY_PATH;

const getRequestPrefix = () =>
  typeof window === 'undefined'
    ? process.env.NEXT_PUBLIC_APP_SERVER_URL || ''
    : '';

const postConferenceApi = async <T>(
  path: string,
  body: Record<string, unknown>,
  site: ConferenceSite = 'zh'
) => {
  try {
    const response = await fetch(
      getRequestPrefix() + getApiPrefix(site) + path,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify(body),
        cache: 'no-store',
      }
    );
    const text = await response.text();

    // 年份切换是增强加载，接口异常、空 body 或非 JSON 时返回 undefined，由调用方落到空模块数据。
    if (!response.ok || !text) {
      return undefined;
    }

    const payload = JSON.parse(text) as PortalBaseResp<T>;
    return payload?.code === 0
      ? markConferenceRequestSuccess(payload.data)
      : undefined;
  } catch (error) {
    console.error('[conference postConferenceApi error]', path, error);
    return undefined;
  }
};

const normalizeConferenceMomentItem = (
  item: ConferenceMomentItem
): ConferenceMomentItem => ({
  momentId: item.momentId,
  imageUrl: item.imageUrl,
  imageFileName: item.imageFileName,
  sortOrder: item.sortOrder,
});

const normalizeConferenceMomentData = (
  data?: ConferenceMomentListResp | ConferenceMomentItem[]
): ConferenceMomentListResp => {
  const list = Array.isArray(data) ? data : data?.momentList || [];

  return {
    momentList: list
      .map(normalizeConferenceMomentItem)
      .filter((item) => item.imageUrl),
  };
};

// fetchConferencePartnerList 拉取合作伙伴列表数据。
// 合作伙伴当前不按届次区分，因此单独拉取，不跟随年份切换重复请求。
export const fetchConferencePartnerList = async (
  site: ConferenceSite = 'zh'
): Promise<ConferencePartnerListResp | undefined> =>
  postConferenceApi<ConferencePartnerListResp>(
    CONFERENCE_PARTNER_LIST_PATH,
    {},
    site
  );

const createEmptyConferenceNewsListResp = (
  conferenceYear: number
): ConferenceNewsListResp => ({
  list: [],
  conferenceYear,
  total: 0,
  page: 1,
  pageSize: 3,
});

// fetchConferenceNewsListAll 一次请求当前届次全部资讯，PC 分页只在前端展示层处理。
const fetchConferenceNewsListAll = async ({
  conferenceYear,
  site = 'zh',
}: {
  conferenceYear: number;
  site?: ConferenceSite;
}): Promise<ConferenceNewsListResp> => {
  const data = await postConferenceApi<ConferenceNewsListResp>(
    CONFERENCE_NEWS_LIST_PATH,
    {
      conferenceYear,
      fetchAll: true,
    },
    site
  );

  return (
    data || {
      list: [],
      conferenceYear,
      total: 0,
      page: 1,
      pageSize: 3,
    }
  );
};

const normalizeYearList = (years?: number[]) =>
  (years || [])
    .filter((year): year is number => typeof year === 'number')
    .filter((year, index, list) => list.indexOf(year) === index);

export const fetchConferenceAiForGoodByYear = async ({
  conferenceYear,
  site = 'zh',
}: FetchConferenceDataParams): Promise<ConferenceAiForGoodListResp> => {
  const data = await postConferenceApi<ConferenceAiForGoodListResp>(
    CONFERENCE_AI_FOR_GOOD_LIST_PATH,
    {
      conferenceYear,
    },
    site
  );

  return data || { list: [], conferenceYear, total: 0 };
};

export const fetchConferenceExhibitionByYear = async ({
  conferenceYear,
  site = 'zh',
}: FetchConferenceDataParams): Promise<ConferenceExhibitionListResp> => {
  const data = await postConferenceApi<ConferenceExhibitionListResp>(
    CONFERENCE_EXHIBITION_LIST_PATH,
    {
      conferenceYear,
    },
    site
  );

  return data || { list: [], conferenceYear, total: 0 };
};

export const fetchConferenceNewsByYear = async ({
  conferenceYear,
  site = 'zh',
}: FetchConferenceDataParams): Promise<ConferenceNewsListResp> =>
  fetchConferenceNewsListAll({
    conferenceYear,
    site,
  });

export const fetchConferenceGuestsByYear = async ({
  conferenceYear,
  site = 'zh',
}: FetchConferenceDataParams): Promise<ConferenceGuestGroupListResp> => {
  const data = await postConferenceApi<ConferenceGuestGroupListResp>(
    CONFERENCE_GUEST_GROUP_LIST_PATH,
    {
      ConferenceYear: conferenceYear,
    },
    site
  );

  return data || { list: [] };
};

export const fetchConferenceVideosByYear = async ({
  conferenceYear,
  site = 'zh',
}: FetchConferenceDataParams): Promise<ConferenceVideoListResp> => {
  const data = await postConferenceApi<ConferenceVideoListResp>(
    CONFERENCE_VIDEO_LIST_PATH,
    {
      ConferenceYear: conferenceYear,
      page: 1,
      pageSize: 100,
    },
    site
  );

  return data || { list: [], total: 0, page: 1, pageSize: 100 };
};

const hasConferenceGuestData = (data?: ConferenceGuestGroupListResp) =>
  (data?.list || []).some((group) => (group?.Guests || []).length > 0);

const hasConferenceVideoData = (data?: ConferenceVideoListResp) =>
  Boolean(data?.list?.length);

const getConferenceAgendaForumList = (data?: ConferenceAgendaDetailResp) =>
  data?.ForumList ||
  data?.forumList ||
  data?.Forums ||
  data?.forums ||
  data?.list ||
  [];

const getConferenceAgendaSessionList = (forum?: any) =>
  forum?.SessionList ||
  forum?.sessionList ||
  forum?.Sessions ||
  forum?.sessions ||
  [];

const getConferenceAgendaItemList = (session?: any) =>
  session?.AgendaList ||
  session?.agendaList ||
  session?.Agendas ||
  session?.agendas ||
  session?.Items ||
  session?.items ||
  [];

const hasConferenceAgendaData = (data?: ConferenceAgendaDetailResp) =>
  getConferenceAgendaForumList(data).some((forum: any) =>
    getConferenceAgendaSessionList(forum).some(
      (session: any) => getConferenceAgendaItemList(session).length > 0
    )
  );

const resolveConferenceSectionYears = async <T>({
  years,
  fetcher,
  hasData,
  defaultYear,
  defaultData,
}: {
  years: number[];
  fetcher: (conferenceYear: number) => Promise<T>;
  hasData: (data?: T) => boolean;
  defaultYear: number;
  defaultData?: T;
}) => {
  const uniqueYears = normalizeYearList(years);
  if (!uniqueYears.length) {
    return {
      years: [],
      dataByYear: new Map<number, T>(),
      successful: true,
    };
  }

  const entries = await Promise.all(
    uniqueYears.map(async (year) => {
      const data =
        year === defaultYear && defaultData !== undefined && hasData(defaultData)
          ? defaultData
          : await fetcher(year);
      return [year, data] as const;
    })
  );
  const validEntries = entries.filter(([, data]) => hasData(data));

  return {
    years: validEntries.map(([year]) => year),
    dataByYear: new Map(validEntries),
    successful: entries.every(([, data]) => isConferenceRequestSuccessful(data)),
  };
};

export const resolveConferenceCoreSectionYears = async ({
  guestYears,
  agendaYears,
  videoYears,
  defaultYear,
  defaultData,
  site = 'zh',
}: {
  guestYears?: number[];
  agendaYears?: number[];
  videoYears?: number[];
  defaultYear: number;
  defaultData: Pick<ConferenceDynamicData, 'guests' | 'agenda' | 'videos'>;
  site?: ConferenceSite;
}) => {
  const [guests, agenda, videos] = await Promise.all([
    resolveConferenceSectionYears({
      years: guestYears || [],
      defaultYear,
      defaultData: defaultData.guests,
      fetcher: (conferenceYear) =>
        fetchConferenceGuestsByYear({ conferenceYear, site }),
      hasData: hasConferenceGuestData,
    }),
    resolveConferenceSectionYears({
      years: agendaYears || [],
      defaultYear,
      defaultData: defaultData.agenda,
      fetcher: (conferenceYear) =>
        fetchConferenceAgendaDetail({ conferenceYear, site }),
      hasData: hasConferenceAgendaData,
    }),
    resolveConferenceSectionYears({
      years: videoYears || [],
      defaultYear,
      defaultData: defaultData.videos,
      fetcher: (conferenceYear) =>
        fetchConferenceVideosByYear({ conferenceYear, site }),
      hasData: hasConferenceVideoData,
    }),
  ]);

  return {
    guests,
    agenda,
    videos,
  };
};

// fetchConferenceDynamicData 拉取首屏默认年份的官网动态配置数据。
// 行业报告和合作伙伴为全局配置；嘉宾、视频等按年份模块后续可独立切换。
export const fetchConferenceDynamicData = async ({
  conferenceYear,
  site = 'zh',
  reportsData,
  partnersData,
}: FetchConferenceDataParams): Promise<ConferenceDynamicData> => {
  const [guests, videos, reports, partners] = await Promise.all([
    fetchConferenceGuestsByYear({ conferenceYear, site }),
    fetchConferenceVideosByYear({ conferenceYear, site }),
    reportsData === undefined
      ? fetchConferenceReportList(site)
      : Promise.resolve(reportsData),
    partnersData === undefined
      ? fetchConferencePartnerList(site)
      : Promise.resolve(partnersData),
  ]);

  return {
    guests,
    videos,
    reports: reports || { list: [], total: 0, page: 1, pageSize: 20 },
    partners: partners || { cooperateCategoryList: [] },
    agenda: { list: [] },
    aiForGood: { list: [], conferenceYear, total: 0 },
    exhibition: { list: [], conferenceYear, total: 0 },
    news: createEmptyConferenceNewsListResp(conferenceYear),
  };
};

// 行业报告与届次无关，SSR 可在年份主接口返回前并发预取。
export const fetchConferenceReportList = async (
  site: ConferenceSite = 'zh'
): Promise<IndustryReportListResp | undefined> =>
  postConferenceApi<IndustryReportListResp>(
    CONFERENCE_REPORT_LIST_PATH,
    {
      page: 1,
      pageSize: 20,
    },
    site
  );

// fetchConferenceYearOptions 拉取大会年份主数据，年份下拉以后台启用年份为准，不在前端写死。
export const fetchConferenceYearOptions = async (
  site: ConferenceSite = 'zh'
): Promise<ConferenceYearListResp> => {
  const data = await postConferenceApi<ConferenceYearListResp>(
    CONFERENCE_YEAR_LIST_PATH,
    {},
    site
  );

  const years = normalizeYearList(
    data?.Years ||
      data?.list
        ?.map((item) => item.ConferenceYear)
        .filter((year): year is number => typeof year === 'number')
  );
  const defaultYear =
    data?.DefaultYear || years?.[0] || new Date().getFullYear();

  const result = {
    Years: years?.length ? years : [defaultYear],
    DefaultYear: defaultYear,
    GuestYears: normalizeYearList(data?.GuestYears),
    AgendaYears: normalizeYearList(data?.AgendaYears),
    VideoYears: normalizeYearList(data?.VideoYears),
    AiForGoodYears: normalizeYearList(data?.AiForGoodYears),
    ExhibitionYears: normalizeYearList(data?.ExhibitionYears),
    NewsYears: normalizeYearList(data?.NewsYears),
    MomentYears: normalizeYearList(data?.MomentYears),
    list: data?.list || [],
  };
  return isConferenceRequestSuccessful(data)
    ? markConferenceRequestSuccess(result)
    : result;
};

// fetchConferenceAgendaDetail 拉取当前届次的大会议程树；接口路径由站点自动切换中英文 BFF。
export const fetchConferenceAgendaDetail = async ({
  conferenceYear,
  site = 'zh',
}: FetchConferenceDataParams): Promise<ConferenceAgendaDetailResp> => {
  const data = await postConferenceApi<ConferenceAgendaDetailResp>(
    CONFERENCE_AGENDA_DETAIL_PATH,
    {
      ConferenceYear: conferenceYear,
    },
    site
  );
  return data || { list: [] };
};

// fetchConferenceTheme 拉取已发布的大会主题配置（全局唯一）
// 后端可能返回扁平对象或 {dbName, themes: [...]} 包装格式，统一解包为单个 ConferenceThemeItem
export const fetchConferenceTheme = async (
  site: ConferenceSite = 'zh'
): Promise<ConferenceThemeItem | undefined> => {
  const raw = await postConferenceApi<Record<string, unknown>>(
    CONFERENCE_THEME_PATH,
    {},
    site
  );
  if (!raw) return undefined;

  // 兼容 {dbName, themes: Array} 包装格式：取第一条
  const themes = raw.themes;
  if (Array.isArray(themes) && themes.length > 0) {
    return markConferenceRequestSuccess(themes[0] as ConferenceThemeItem);
  }
  // 扁平对象直接返回
  return raw as unknown as ConferenceThemeItem;
};

// fetchConferenceHighlights 拉取已上线的大会亮点列表
export const fetchConferenceHighlights = async (
  site: ConferenceSite = 'zh'
): Promise<ConferenceHighlightItem[]> => {
  const data = await postConferenceApi<ConferenceHighlightItem[]>(
    CONFERENCE_HIGHLIGHTS_PATH,
    { site },
    site
  );
  return data || [];
};

// fetchConferenceBrands 拉取已上线的品牌营销内容
export const fetchConferenceBrands = async (
  site: ConferenceSite = 'zh'
): Promise<ConferenceBrandItem[] | undefined> =>
  postConferenceApi<ConferenceBrandItem[]>(CONFERENCE_BRANDS_PATH, {}, site);

export const fetchConferenceMoments = async ({
  conferenceYear,
  site = 'zh',
}: FetchConferenceDataParams): Promise<ConferenceMomentListResp> => {
  try {
    const response = await fetch(
      getRequestPrefix() + getMomentDisplayPath(site),
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify({ conferenceYear }),
        cache: 'no-store',
      }
    );
    const text = await response.text();

    if (!response.ok || !text) {
      return { momentList: [] };
    }

    const payload = JSON.parse(text) as PortalBaseResp<
      ConferenceMomentListResp | ConferenceMomentItem[]
    >;
    return payload?.code === 0
      ? markConferenceRequestSuccess(normalizeConferenceMomentData(payload.data))
      : { momentList: [] };
  } catch (error) {
    console.error('[conference fetchConferenceMoments error]', error);
    return { momentList: [] };
  }
};

export const normalizeConferenceMoments = (
  data?: ConferenceMomentListResp | ConferenceMomentItem[]
) => normalizeConferenceMomentData(data);

// isConferenceSignupSurveyUrl 判断按钮链接是否为腾讯问卷原始填写链接。
export const isConferenceSignupSurveyUrl = (rawUrl?: string) => {
  if (!rawUrl) {
    return false;
  }
  try {
    const baseOrigin =
      typeof window === 'undefined'
        ? 'https://ssv.tencent.com'
        : window.location.origin;
    const parsedUrl = new URL(rawUrl, baseOrigin);
    return (
      parsedUrl.hostname.endsWith('wj.qq.com') &&
      parsedUrl.pathname.includes('/s2/')
    );
  } catch (error) {
    return false;
  }
};

// saveConferenceSignupPendingAction 记录一次待续跳的报名动作，用于登录回跳后自动继续进入问卷。
export const saveConferenceSignupPendingAction = (
  action: ConferenceSignupPendingAction
) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.setItem(
    CONFERENCE_SIGNUP_PENDING_KEY,
    JSON.stringify(action)
  );
};

export const loadConferenceSignupPendingAction = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const raw = window.sessionStorage.getItem(CONFERENCE_SIGNUP_PENDING_KEY);
  if (!raw) {
    return undefined;
  }
  try {
    return JSON.parse(raw) as ConferenceSignupPendingAction;
  } catch (error) {
    return undefined;
  }
};

export const clearConferenceSignupPendingAction = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.removeItem(CONFERENCE_SIGNUP_PENDING_KEY);
};

// redirectConferenceSignupLogin 统一复用站点当前页面作为登录回跳地址。
export const redirectConferenceSignupLogin = (site: ConferenceSite = 'zh') => {
  if (typeof window === 'undefined') {
    return;
  }
  const langParam = site === 'en' ? '&s_language=en' : '';
  window.location.href = `/login?s_url=${encodeURIComponent(
    window.location.href
  )}${langParam}`;
};

// requestConferenceSignupRedirect 向 portal 获取最终问卷跳转地址，仅在真实登录态错误时触发登录回跳。
export const requestConferenceSignupRedirect = async ({
  channelId,
  surveyUrl,
}: {
  channelId?: number | string;
  surveyUrl?: string;
}): Promise<ConferenceSignupRedirectResult> => {
  const searchParams = new URLSearchParams();
  if (channelId !== undefined && channelId !== null && `${channelId}`.trim()) {
    searchParams.set('channelId', `${channelId}`.trim());
  }
  if (surveyUrl?.trim()) {
    searchParams.set('surveyUrl', surveyUrl.trim());
  }

  try {
    const response = await fetch(
      `${getRequestPrefix()}${CONFERENCE_SIGNUP_REDIRECT_PATH}?${searchParams.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
      }
    );

    const text = await response.text();
    if (!text) {
      if (response.status === 401) {
        return { kind: 'login' };
      }
      return {
        kind: 'error',
        message: CONFERENCE_SIGNUP_FALLBACK_ERROR_MESSAGE,
      };
    }

    const payload = JSON.parse(text) as PortalBaseResp<{ url?: string }>;
    return parseConferenceSignupRedirectPayload(payload);
  } catch (error) {
    console.error('[conference requestConferenceSignupRedirect error]', error);
    return { kind: 'error', message: CONFERENCE_SIGNUP_FALLBACK_ERROR_MESSAGE };
  }
};
